import { generateMockAnalysis } from './mockAnalysis';

const DEMO_VEHICLE_1 = {
  id: 'demo-vehicle-1',
  garageId: 'demo-garage-1',
  year: '2021',
  make: 'Toyota',
  model: 'RAV4',
  trim: 'XLE Premium',
  mileage: 38500,
  tmu: false,
  condition: 'Good',
  primaryUse: ['Daily Commute'],
  skillLevel: 'Intermediate',
  photo: null,
};

const DEMO_VEHICLE_2 = {
  id: 'demo-vehicle-2',
  garageId: 'demo-garage-1',
  year: '2019',
  make: 'Ford',
  model: 'Mustang GT',
  trim: 'Premium',
  mileage: 24200,
  tmu: false,
  condition: 'Excellent',
  primaryUse: ['Weekend Drives'],
  skillLevel: 'Intermediate',
  photo: null,
};

const DEMO_GARAGE = {
  id: 'demo-garage-1',
  name: 'My Garage',
  photo: null,
  vehicleIds: ['demo-vehicle-1', 'demo-vehicle-2'],
  location: '',
  owner: 'alex@example.com',
  collaborators: [],
};

export function populateDemoData() {
  // User profile
  localStorage.setItem('pm_user_profile', JSON.stringify({
    firstName: 'Alex',
    lastName: 'Demo',
    email: 'alex@example.com',
    zipCode: '90210',
    yearOfBirth: '1990',
    salaryRange: '',
    mechanicalLevel: 'Intermediate',
    primaryUseCases: ['Daily Commute', 'Weekend Drives'],
    referralSource: 'App Review',
    createdAt: new Date().toISOString(),
    legalAcceptances: {
      termsVersion: '1.0',
      termsAcceptedAt: new Date().toISOString(),
      privacyVersion: '1.0',
      privacyAcceptedAt: new Date().toISOString(),
      safetyVersion: '1.0',
      safetyAcceptedAt: new Date().toISOString(),
    },
  }));

  // Garages
  localStorage.setItem('pm_garages', JSON.stringify([DEMO_GARAGE]));

  // Vehicles
  localStorage.setItem('pm_vehicles', JSON.stringify([DEMO_VEHICLE_1, DEMO_VEHICLE_2]));

  // Analyses (using existing mockAnalysis utility)
  const analyses = {
    'demo-vehicle-1': {
      ...generateMockAnalysis(DEMO_VEHICLE_1),
      analyzedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    },
    'demo-vehicle-2': {
      ...generateMockAnalysis(DEMO_VEHICLE_2),
      analyzedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    },
  };
  localStorage.setItem('pm_analyses', JSON.stringify(analyses));

  // Service history (realistic entries)
  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;
  const serviceHistory = [
    // RAV4 entries
    {
      id: 'svc-demo-1',
      vehicleId: 'demo-vehicle-1',
      service: 'Oil & Filter Change',
      date: new Date(now - 30 * DAY).toISOString().split('T')[0],
      mileage: 37800,
      cost: 62,
      provider: 'Valvoline Instant Oil Change',
      notes: 'Full synthetic 0W-20, new OEM filter',
    },
    {
      id: 'svc-demo-2',
      vehicleId: 'demo-vehicle-1',
      service: 'Tire Rotation',
      date: new Date(now - 60 * DAY).toISOString().split('T')[0],
      mileage: 36200,
      cost: 30,
      provider: 'Discount Tire',
      notes: 'Rotated front to rear, checked pressure',
    },
    {
      id: 'svc-demo-3',
      vehicleId: 'demo-vehicle-1',
      service: 'Cabin Air Filter',
      date: new Date(now - 120 * DAY).toISOString().split('T')[0],
      mileage: 33000,
      cost: 18,
      provider: 'DIY',
      notes: 'Replaced with OEM filter from Amazon',
    },
    // Mustang entries
    {
      id: 'svc-demo-4',
      vehicleId: 'demo-vehicle-2',
      service: 'Oil & Filter Change',
      date: new Date(now - 45 * DAY).toISOString().split('T')[0],
      mileage: 23800,
      cost: 75,
      provider: 'Ford Dealership',
      notes: 'Motorcraft full synthetic 5W-20',
    },
    {
      id: 'svc-demo-5',
      vehicleId: 'demo-vehicle-2',
      service: 'Brake Pad Replacement (Front)',
      date: new Date(now - 90 * DAY).toISOString().split('T')[0],
      mileage: 22500,
      cost: 280,
      provider: 'Independent Shop',
      notes: 'Replaced with ceramic pads, rotors resurfaced',
    },
    {
      id: 'svc-demo-6',
      vehicleId: 'demo-vehicle-2',
      service: 'Tire Rotation & Balance',
      date: new Date(now - 150 * DAY).toISOString().split('T')[0],
      mileage: 21000,
      cost: 55,
      provider: 'Discount Tire',
      notes: 'All four balanced and rotated, good tread remaining',
    },
  ];
  localStorage.setItem('pm_service_history', JSON.stringify(serviceHistory));

  // Reminders (upcoming)
  const reminders = [
    {
      id: 'rem-demo-1',
      vehicleId: 'demo-vehicle-1',
      title: 'Oil Change Due',
      description: 'Next oil change at 43,500 miles',
      dueDate: new Date(now + 15 * DAY).toISOString().split('T')[0],
      dueTime: null,
      status: 'pending',
      completedAt: null,
      createdAt: new Date(now - 5 * DAY).toISOString(),
    },
    {
      id: 'rem-demo-2',
      vehicleId: 'demo-vehicle-1',
      title: 'Annual Inspection',
      description: 'State safety and emissions inspection',
      dueDate: new Date(now + 45 * DAY).toISOString().split('T')[0],
      dueTime: '09:00',
      status: 'pending',
      completedAt: null,
      createdAt: new Date(now - 10 * DAY).toISOString(),
    },
    {
      id: 'rem-demo-3',
      vehicleId: 'demo-vehicle-2',
      title: 'Oil Change Due',
      description: 'Next oil change at 29,000 miles',
      dueDate: new Date(now + 30 * DAY).toISOString().split('T')[0],
      dueTime: null,
      status: 'pending',
      completedAt: null,
      createdAt: new Date(now - 3 * DAY).toISOString(),
    },
  ];
  localStorage.setItem('pm_reminders', JSON.stringify(reminders));

  // Settings (Enthusiast tier)
  localStorage.setItem('pm_settings', JSON.stringify({
    profile: { name: 'Alex Demo', email: 'alex@example.com' },
    subscription: { plan: 'showroom', creditsUsed: 0, creditsTotal: 9999 },
    preferences: { units: 'miles', currency: 'USD', notifyMaintenance: true, notifyMarket: false, showEvents: false, pushReminders: true, pushAnalysisComplete: true, pushMileageCheckin: true },
  }));

  // Empty keys for completeness (prevent stale data)
  localStorage.setItem('pm_chat_histories', JSON.stringify({}));
  localStorage.setItem('pm_aa_analyses', JSON.stringify([]));
  localStorage.setItem('pm_mileage_logs', JSON.stringify({}));
  localStorage.setItem('pm_dismissed_items', JSON.stringify({}));
  localStorage.setItem('pm_diy_guides', JSON.stringify({}));
  localStorage.setItem('pm_aa_checklist', JSON.stringify({}));
  localStorage.setItem('pm_value_history', JSON.stringify({}));
}

/* ───────────────────── Business / Team Demo ───────────────────── */

const BIZ_GARAGES = [
  { id: 'biz-garage-hq',  name: 'Headquarters',  location: 'Downtown' },
  { id: 'biz-garage-loc2', name: 'Location 2',    location: 'Northside' },
  { id: 'biz-garage-loc3', name: 'Location 3',    location: 'Eastside' },
  { id: 'biz-garage-vip',  name: 'VIP Customers', location: 'Marina District' },
  { id: 'biz-garage-svc',  name: 'In Service',    location: 'Service Center' },
];

// 10 vehicles per garage — realistic fleet mix
const BIZ_VEHICLES = [
  // ── Headquarters (executive fleet) ──
  { id: 'biz-v-01', garageId: 'biz-garage-hq', year: '2024', make: 'BMW', model: '530i', trim: 'xDrive', mileage: 8200, condition: 'Excellent', primaryUse: ['Business'] },
  { id: 'biz-v-02', garageId: 'biz-garage-hq', year: '2023', make: 'Mercedes-Benz', model: 'E 350', trim: '4MATIC', mileage: 15400, condition: 'Excellent', primaryUse: ['Business'] },
  { id: 'biz-v-03', garageId: 'biz-garage-hq', year: '2024', make: 'Audi', model: 'A6', trim: 'Premium Plus', mileage: 6100, condition: 'Excellent', primaryUse: ['Business'] },
  { id: 'biz-v-04', garageId: 'biz-garage-hq', year: '2023', make: 'Lexus', model: 'ES 350', trim: 'F Sport', mileage: 18700, condition: 'Good', primaryUse: ['Business'] },
  { id: 'biz-v-05', garageId: 'biz-garage-hq', year: '2024', make: 'Genesis', model: 'G80', trim: '2.5T', mileage: 4300, condition: 'Excellent', primaryUse: ['Business'] },
  { id: 'biz-v-06', garageId: 'biz-garage-hq', year: '2023', make: 'Cadillac', model: 'CT5', trim: 'Premium Luxury', mileage: 21000, condition: 'Good', primaryUse: ['Business'] },
  { id: 'biz-v-07', garageId: 'biz-garage-hq', year: '2024', make: 'BMW', model: 'X5', trim: 'xDrive40i', mileage: 9800, condition: 'Excellent', primaryUse: ['Business'] },
  { id: 'biz-v-08', garageId: 'biz-garage-hq', year: '2023', make: 'Mercedes-Benz', model: 'GLC 300', trim: '4MATIC', mileage: 17200, condition: 'Good', primaryUse: ['Business'] },
  { id: 'biz-v-09', garageId: 'biz-garage-hq', year: '2024', make: 'Audi', model: 'Q5', trim: 'S Line', mileage: 5600, condition: 'Excellent', primaryUse: ['Business'] },
  { id: 'biz-v-10', garageId: 'biz-garage-hq', year: '2023', make: 'Volvo', model: 'S60', trim: 'Recharge', mileage: 14800, condition: 'Good', primaryUse: ['Business'] },

  // ── Location 2 (work trucks & utility) ──
  { id: 'biz-v-11', garageId: 'biz-garage-loc2', year: '2022', make: 'Ford', model: 'F-150', trim: 'XLT', mileage: 42300, condition: 'Good', primaryUse: ['Work'] },
  { id: 'biz-v-12', garageId: 'biz-garage-loc2', year: '2023', make: 'Chevrolet', model: 'Silverado 1500', trim: 'LT', mileage: 28900, condition: 'Good', primaryUse: ['Work'] },
  { id: 'biz-v-13', garageId: 'biz-garage-loc2', year: '2021', make: 'Ram', model: '1500', trim: 'Big Horn', mileage: 51200, condition: 'Fair', primaryUse: ['Work'] },
  { id: 'biz-v-14', garageId: 'biz-garage-loc2', year: '2023', make: 'Toyota', model: 'Tacoma', trim: 'SR5', mileage: 19800, condition: 'Good', primaryUse: ['Work'] },
  { id: 'biz-v-15', garageId: 'biz-garage-loc2', year: '2022', make: 'Ford', model: 'Ranger', trim: 'Lariat', mileage: 35600, condition: 'Good', primaryUse: ['Work'] },
  { id: 'biz-v-16', garageId: 'biz-garage-loc2', year: '2021', make: 'Chevrolet', model: 'Colorado', trim: 'Z71', mileage: 44100, condition: 'Fair', primaryUse: ['Work'] },
  { id: 'biz-v-17', garageId: 'biz-garage-loc2', year: '2023', make: 'GMC', model: 'Sierra 1500', trim: 'SLE', mileage: 22400, condition: 'Good', primaryUse: ['Work'] },
  { id: 'biz-v-18', garageId: 'biz-garage-loc2', year: '2022', make: 'Toyota', model: 'Tundra', trim: 'SR5', mileage: 38700, condition: 'Good', primaryUse: ['Work'] },
  { id: 'biz-v-19', garageId: 'biz-garage-loc2', year: '2023', make: 'Ford', model: 'F-250', trim: 'XLT Super Duty', mileage: 31500, condition: 'Good', primaryUse: ['Work'] },
  { id: 'biz-v-20', garageId: 'biz-garage-loc2', year: '2021', make: 'Ram', model: '2500', trim: 'Tradesman', mileage: 58400, condition: 'Fair', primaryUse: ['Work'] },

  // ── Location 3 (delivery & fleet vans) ──
  { id: 'biz-v-21', garageId: 'biz-garage-loc3', year: '2023', make: 'Ford', model: 'Transit', trim: '250 Cargo', mileage: 27300, condition: 'Good', primaryUse: ['Delivery'] },
  { id: 'biz-v-22', garageId: 'biz-garage-loc3', year: '2022', make: 'Mercedes-Benz', model: 'Sprinter', trim: '2500 Cargo', mileage: 41200, condition: 'Good', primaryUse: ['Delivery'] },
  { id: 'biz-v-23', garageId: 'biz-garage-loc3', year: '2023', make: 'Ram', model: 'ProMaster', trim: '2500 High Roof', mileage: 33800, condition: 'Good', primaryUse: ['Delivery'] },
  { id: 'biz-v-24', garageId: 'biz-garage-loc3', year: '2022', make: 'Ford', model: 'E-Transit', trim: '350 Cargo', mileage: 19600, condition: 'Good', primaryUse: ['Delivery'] },
  { id: 'biz-v-25', garageId: 'biz-garage-loc3', year: '2023', make: 'Chevrolet', model: 'Express', trim: '2500 Cargo', mileage: 29400, condition: 'Good', primaryUse: ['Delivery'] },
  { id: 'biz-v-26', garageId: 'biz-garage-loc3', year: '2021', make: 'Nissan', model: 'NV200', trim: 'S', mileage: 52100, condition: 'Fair', primaryUse: ['Delivery'] },
  { id: 'biz-v-27', garageId: 'biz-garage-loc3', year: '2023', make: 'Ford', model: 'Transit Connect', trim: 'XLT Cargo', mileage: 18900, condition: 'Good', primaryUse: ['Delivery'] },
  { id: 'biz-v-28', garageId: 'biz-garage-loc3', year: '2022', make: 'Ram', model: 'ProMaster City', trim: 'Tradesman', mileage: 36700, condition: 'Good', primaryUse: ['Delivery'] },
  { id: 'biz-v-29', garageId: 'biz-garage-loc3', year: '2023', make: 'Mercedes-Benz', model: 'Sprinter', trim: '1500 Cargo', mileage: 24500, condition: 'Good', primaryUse: ['Delivery'] },
  { id: 'biz-v-30', garageId: 'biz-garage-loc3', year: '2022', make: 'Ford', model: 'Transit', trim: '150 Cargo', mileage: 38200, condition: 'Fair', primaryUse: ['Delivery'] },

  // ── VIP Customers (luxury / enthusiast) ──
  { id: 'biz-v-31', garageId: 'biz-garage-vip', year: '2024', make: 'Porsche', model: '911 Carrera S', trim: '', mileage: 3200, condition: 'Excellent', primaryUse: ['Weekend Drives'] },
  { id: 'biz-v-32', garageId: 'biz-garage-vip', year: '2023', make: 'BMW', model: 'M4 Competition', trim: 'xDrive', mileage: 8900, condition: 'Excellent', primaryUse: ['Weekend Drives'] },
  { id: 'biz-v-33', garageId: 'biz-garage-vip', year: '2024', make: 'Mercedes-AMG', model: 'GT 53', trim: '4MATIC+', mileage: 4100, condition: 'Excellent', primaryUse: ['Weekend Drives'] },
  { id: 'biz-v-34', garageId: 'biz-garage-vip', year: '2023', make: 'Land Rover', model: 'Range Rover', trim: 'SE', mileage: 12600, condition: 'Excellent', primaryUse: ['Daily Commute'] },
  { id: 'biz-v-35', garageId: 'biz-garage-vip', year: '2024', make: 'Porsche', model: 'Cayenne', trim: 'S', mileage: 5800, condition: 'Excellent', primaryUse: ['Daily Commute'] },
  { id: 'biz-v-36', garageId: 'biz-garage-vip', year: '2023', make: 'Audi', model: 'RS 5', trim: 'Sportback', mileage: 11200, condition: 'Excellent', primaryUse: ['Weekend Drives'] },
  { id: 'biz-v-37', garageId: 'biz-garage-vip', year: '2024', make: 'Lexus', model: 'LC 500', trim: '', mileage: 2900, condition: 'Excellent', primaryUse: ['Weekend Drives'] },
  { id: 'biz-v-38', garageId: 'biz-garage-vip', year: '2023', make: 'Maserati', model: 'Ghibli', trim: 'Modena', mileage: 9400, condition: 'Good', primaryUse: ['Daily Commute'] },
  { id: 'biz-v-39', garageId: 'biz-garage-vip', year: '2024', make: 'BMW', model: 'X7', trim: 'xDrive40i', mileage: 6700, condition: 'Excellent', primaryUse: ['Daily Commute'] },
  { id: 'biz-v-40', garageId: 'biz-garage-vip', year: '2023', make: 'Mercedes-Benz', model: 'S 580', trim: '4MATIC', mileage: 14300, condition: 'Excellent', primaryUse: ['Business'] },

  // ── In Service (vehicles being worked on — mixed conditions) ──
  { id: 'biz-v-41', garageId: 'biz-garage-svc', year: '2022', make: 'Ford', model: 'F-150', trim: 'Lariat', mileage: 48200, condition: 'Fair', primaryUse: ['Work'] },
  { id: 'biz-v-42', garageId: 'biz-garage-svc', year: '2021', make: 'BMW', model: '330i', trim: 'xDrive', mileage: 52100, condition: 'Fair', primaryUse: ['Business'] },
  { id: 'biz-v-43', garageId: 'biz-garage-svc', year: '2023', make: 'Chevrolet', model: 'Suburban', trim: 'LT', mileage: 31400, condition: 'Good', primaryUse: ['Business'] },
  { id: 'biz-v-44', garageId: 'biz-garage-svc', year: '2020', make: 'Mercedes-Benz', model: 'Sprinter', trim: '3500 Cargo', mileage: 67800, condition: 'Fair', primaryUse: ['Delivery'] },
  { id: 'biz-v-45', garageId: 'biz-garage-svc', year: '2022', make: 'Toyota', model: 'Camry', trim: 'SE', mileage: 39500, condition: 'Good', primaryUse: ['Daily Commute'] },
  { id: 'biz-v-46', garageId: 'biz-garage-svc', year: '2021', make: 'Ram', model: '1500', trim: 'Laramie', mileage: 55600, condition: 'Fair', primaryUse: ['Work'] },
  { id: 'biz-v-47', garageId: 'biz-garage-svc', year: '2023', make: 'Audi', model: 'Q7', trim: 'Premium', mileage: 22800, condition: 'Good', primaryUse: ['Business'] },
  { id: 'biz-v-48', garageId: 'biz-garage-svc', year: '2020', make: 'Ford', model: 'Transit', trim: '350 Cargo', mileage: 71200, condition: 'Fair', primaryUse: ['Delivery'] },
  { id: 'biz-v-49', garageId: 'biz-garage-svc', year: '2022', make: 'Porsche', model: 'Macan', trim: 'S', mileage: 28900, condition: 'Good', primaryUse: ['Weekend Drives'] },
  { id: 'biz-v-50', garageId: 'biz-garage-svc', year: '2021', make: 'Lexus', model: 'RX 350', trim: 'F Sport', mileage: 43700, condition: 'Good', primaryUse: ['Daily Commute'] },
].map((v) => ({ ...v, tmu: false, skillLevel: 'Advanced', photo: null }));

export function populateBusinessDemoData() {
  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;

  // User profile
  localStorage.setItem('pm_user_profile', JSON.stringify({
    firstName: 'Jordan',
    lastName: 'Fleet Manager',
    email: 'jordan@example.com',
    zipCode: '10001',
    yearOfBirth: '1985',
    salaryRange: '',
    mechanicalLevel: 'Advanced',
    primaryUseCases: ['Fleet Management', 'Business'],
    referralSource: 'Business Demo',
    createdAt: new Date().toISOString(),
    legalAcceptances: {
      termsVersion: '1.0',
      termsAcceptedAt: new Date().toISOString(),
      privacyVersion: '1.0',
      privacyAcceptedAt: new Date().toISOString(),
      safetyVersion: '1.0',
      safetyAcceptedAt: new Date().toISOString(),
    },
  }));

  // Build garages with vehicleIds
  const garages = BIZ_GARAGES.map((g) => ({
    ...g,
    photo: null,
    vehicleIds: BIZ_VEHICLES.filter((v) => v.garageId === g.id).map((v) => v.id),
    owner: 'jordan@example.com',
    collaborators: [],
  }));
  localStorage.setItem('pm_garages', JSON.stringify(garages));

  // Vehicles
  localStorage.setItem('pm_vehicles', JSON.stringify(BIZ_VEHICLES));

  // Analyses for every vehicle
  const analyses = {};
  BIZ_VEHICLES.forEach((v, i) => {
    analyses[v.id] = {
      ...generateMockAnalysis(v),
      analyzedAt: new Date(now - (i + 1) * DAY).toISOString(),
    };
  });
  localStorage.setItem('pm_analyses', JSON.stringify(analyses));

  // Service history — 2 representative entries per garage (10 total)
  const serviceEntries = [
    { vehicleId: 'biz-v-01', service: 'Oil & Filter Change', days: 20, mileage: 7800, cost: 95, provider: 'BMW Dealer', notes: 'Full synthetic 0W-20' },
    { vehicleId: 'biz-v-03', service: 'Tire Rotation', days: 35, mileage: 5500, cost: 40, provider: 'Fleet Tire Service', notes: 'Rotated all four, pressure checked' },
    { vehicleId: 'biz-v-11', service: 'Brake Pad Replacement', days: 15, mileage: 41800, cost: 320, provider: 'Fleet Maintenance', notes: 'Front pads replaced, rotors resurfaced' },
    { vehicleId: 'biz-v-14', service: 'Oil & Filter Change', days: 40, mileage: 19200, cost: 68, provider: 'Toyota Dealer', notes: 'Full synthetic 0W-20, OEM filter' },
    { vehicleId: 'biz-v-21', service: 'Transmission Service', days: 25, mileage: 26500, cost: 240, provider: 'Fleet Maintenance', notes: 'Drain and fill, new filter' },
    { vehicleId: 'biz-v-23', service: 'Oil & Filter Change', days: 30, mileage: 33000, cost: 72, provider: 'Quick Lube', notes: 'Full synthetic 5W-30' },
    { vehicleId: 'biz-v-31', service: 'Annual Inspection', days: 10, mileage: 3100, cost: 150, provider: 'Porsche Dealer', notes: 'All systems nominal, brake fluid topped off' },
    { vehicleId: 'biz-v-34', service: 'Tire Replacement (All 4)', days: 45, mileage: 12000, cost: 1800, provider: 'Tire Rack', notes: 'Michelin Latitude Sport 3, mounted and balanced' },
    { vehicleId: 'biz-v-41', service: 'Engine Diagnostic', days: 5, mileage: 48200, cost: 125, provider: 'Fleet Maintenance', notes: 'Check engine light — O2 sensor replacement scheduled' },
    { vehicleId: 'biz-v-44', service: 'Coolant Flush', days: 8, mileage: 67500, cost: 110, provider: 'Fleet Maintenance', notes: 'Full coolant system flush and refill' },
  ].map((e, i) => ({
    id: `svc-biz-${i + 1}`,
    vehicleId: e.vehicleId,
    service: e.service,
    date: new Date(now - e.days * DAY).toISOString().split('T')[0],
    mileage: e.mileage,
    cost: e.cost,
    provider: e.provider,
    notes: e.notes,
  }));
  localStorage.setItem('pm_service_history', JSON.stringify(serviceEntries));

  // Reminders — spread across garages
  const reminders = [
    { vehicleId: 'biz-v-02', title: 'Oil Change Due', description: 'Mercedes E 350 — 20,000 mile service', days: 10 },
    { vehicleId: 'biz-v-07', title: 'Brake Inspection', description: 'BMW X5 — 15,000 mile check', days: 20 },
    { vehicleId: 'biz-v-13', title: 'Transmission Service', description: 'Ram 1500 — 60,000 mile interval', days: 7 },
    { vehicleId: 'biz-v-22', title: 'DOT Inspection', description: 'Sprinter — annual fleet compliance', days: 30 },
    { vehicleId: 'biz-v-32', title: 'Annual Service', description: 'BMW M4 — customer scheduled pickup', days: 14 },
    { vehicleId: 'biz-v-41', title: 'O2 Sensor Replacement', description: 'F-150 — parts on order', days: 3 },
    { vehicleId: 'biz-v-46', title: 'Timing Belt Replacement', description: 'Ram 1500 — overdue at 55k', days: 5 },
  ].map((r, i) => ({
    id: `rem-biz-${i + 1}`,
    vehicleId: r.vehicleId,
    title: r.title,
    description: r.description,
    dueDate: new Date(now + r.days * DAY).toISOString().split('T')[0],
    dueTime: null,
    status: 'pending',
    completedAt: null,
    createdAt: new Date(now - 5 * DAY).toISOString(),
  }));
  localStorage.setItem('pm_reminders', JSON.stringify(reminders));

  // Settings (Team tier)
  localStorage.setItem('pm_settings', JSON.stringify({
    profile: { name: 'Jordan Fleet Manager', email: 'jordan@example.com' },
    subscription: { plan: 'team', creditsUsed: 87, creditsTotal: 500 },
    preferences: { units: 'miles', currency: 'USD', notifyMaintenance: true, notifyMarket: false, showEvents: false, pushReminders: true, pushAnalysisComplete: true, pushMileageCheckin: true },
  }));

  // Empty keys for completeness
  localStorage.setItem('pm_chat_histories', JSON.stringify({}));
  localStorage.setItem('pm_aa_analyses', JSON.stringify([]));
  localStorage.setItem('pm_mileage_logs', JSON.stringify({}));
  localStorage.setItem('pm_dismissed_items', JSON.stringify({}));
  localStorage.setItem('pm_diy_guides', JSON.stringify({}));
  localStorage.setItem('pm_aa_checklist', JSON.stringify({}));
  localStorage.setItem('pm_value_history', JSON.stringify({}));
}

/* ───────────────────── Club Owner Demo ───────────────────── */

const CLUB_VEHICLE_1 = {
  id: 'club-vehicle-1',
  garageId: 'club-garage-1',
  year: '1967',
  make: 'Ford',
  model: 'Mustang',
  trim: 'Fastback',
  mileage: 78400,
  tmu: true,
  condition: 'Good',
  primaryUse: ['Weekend Drives'],
  skillLevel: 'Advanced',
  photo: null,
};

const CLUB_VEHICLE_2 = {
  id: 'club-vehicle-2',
  garageId: 'club-garage-1',
  year: '2024',
  make: 'Porsche',
  model: '911 Carrera',
  trim: 'T',
  mileage: 4200,
  tmu: false,
  condition: 'Excellent',
  primaryUse: ['Weekend Drives'],
  skillLevel: 'Advanced',
  photo: null,
};

const CLUB_GARAGE = {
  id: 'club-garage-1',
  name: 'My Garage',
  photo: null,
  vehicleIds: ['club-vehicle-1', 'club-vehicle-2'],
  location: '',
  owner: 'morgan@example.com',
  collaborators: [],
};

export function populateClubDemoData() {
  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;

  // User profile
  localStorage.setItem('pm_user_profile', JSON.stringify({
    firstName: 'Morgan',
    lastName: 'Club Demo',
    email: 'morgan@example.com',
    zipCode: '33101',
    yearOfBirth: '1988',
    salaryRange: '',
    mechanicalLevel: 'Advanced',
    primaryUseCases: ['Weekend Drives', 'Car Shows'],
    referralSource: 'Club Demo',
    createdAt: new Date().toISOString(),
    legalAcceptances: {
      termsVersion: '1.0',
      termsAcceptedAt: new Date().toISOString(),
      privacyVersion: '1.0',
      privacyAcceptedAt: new Date().toISOString(),
      safetyVersion: '1.0',
      safetyAcceptedAt: new Date().toISOString(),
    },
  }));

  // Garages
  localStorage.setItem('pm_garages', JSON.stringify([CLUB_GARAGE]));

  // Vehicles
  localStorage.setItem('pm_vehicles', JSON.stringify([CLUB_VEHICLE_1, CLUB_VEHICLE_2]));

  // Analyses
  const analyses = {
    'club-vehicle-1': {
      ...generateMockAnalysis(CLUB_VEHICLE_1),
      analyzedAt: new Date(now - 3 * DAY).toISOString(),
    },
    'club-vehicle-2': {
      ...generateMockAnalysis(CLUB_VEHICLE_2),
      analyzedAt: new Date(now - 7 * DAY).toISOString(),
    },
  };
  localStorage.setItem('pm_analyses', JSON.stringify(analyses));

  // Service history
  const serviceHistory = [
    {
      id: 'svc-club-1',
      vehicleId: 'club-vehicle-1',
      service: 'Carburetor Rebuild',
      date: new Date(now - 45 * DAY).toISOString().split('T')[0],
      mileage: 78200,
      cost: 450,
      provider: 'Classic Restorations',
      notes: 'Full rebuild with new jets, gaskets, and accelerator pump',
    },
    {
      id: 'svc-club-2',
      vehicleId: 'club-vehicle-1',
      service: 'Brake Master Cylinder',
      date: new Date(now - 90 * DAY).toISOString().split('T')[0],
      mileage: 77800,
      cost: 320,
      provider: 'DIY',
      notes: 'Replaced with disc brake conversion master cylinder',
    },
    {
      id: 'svc-club-3',
      vehicleId: 'club-vehicle-2',
      service: 'Oil & Filter Change',
      date: new Date(now - 20 * DAY).toISOString().split('T')[0],
      mileage: 3800,
      cost: 120,
      provider: 'Porsche Dealer',
      notes: 'Mobil 1 0W-40, OEM filter',
    },
    {
      id: 'svc-club-4',
      vehicleId: 'club-vehicle-2',
      service: 'Annual Inspection',
      date: new Date(now - 60 * DAY).toISOString().split('T')[0],
      mileage: 3200,
      cost: 195,
      provider: 'Porsche Dealer',
      notes: 'All systems nominal, brake fluid topped off',
    },
  ];
  localStorage.setItem('pm_service_history', JSON.stringify(serviceHistory));

  // Reminders
  const reminders = [
    {
      id: 'rem-club-1',
      vehicleId: 'club-vehicle-1',
      title: 'Points & Plugs',
      description: 'Replace ignition points, condenser, and spark plugs',
      dueDate: new Date(now + 14 * DAY).toISOString().split('T')[0],
      dueTime: null,
      status: 'pending',
      completedAt: null,
      createdAt: new Date(now - 5 * DAY).toISOString(),
    },
    {
      id: 'rem-club-2',
      vehicleId: 'club-vehicle-2',
      title: 'Tire Rotation',
      description: 'Rotate and check pressure before next track day',
      dueDate: new Date(now + 21 * DAY).toISOString().split('T')[0],
      dueTime: null,
      status: 'pending',
      completedAt: null,
      createdAt: new Date(now - 2 * DAY).toISOString(),
    },
  ];
  localStorage.setItem('pm_reminders', JSON.stringify(reminders));

  // Settings (Showroom tier)
  localStorage.setItem('pm_settings', JSON.stringify({
    profile: { name: 'Morgan Club Demo', email: 'morgan@example.com' },
    subscription: { plan: 'showroom', creditsUsed: 0, creditsTotal: 9999 },
    preferences: { units: 'miles', currency: 'USD', notifyMaintenance: true, notifyMarket: false, showEvents: true, pushReminders: true, pushAnalysisComplete: true, pushMileageCheckin: true },
  }));

  // Empty keys for completeness
  localStorage.setItem('pm_chat_histories', JSON.stringify({}));
  localStorage.setItem('pm_aa_analyses', JSON.stringify([]));
  localStorage.setItem('pm_mileage_logs', JSON.stringify({}));
  localStorage.setItem('pm_dismissed_items', JSON.stringify({}));
  localStorage.setItem('pm_diy_guides', JSON.stringify({}));
  localStorage.setItem('pm_aa_checklist', JSON.stringify({}));
  localStorage.setItem('pm_value_history', JSON.stringify({}));
}

export function clearDemoData() {
  const keys = [
    'pm_garages', 'pm_vehicles', 'pm_analyses', 'pm_service_history',
    'pm_chat_histories', 'pm_aa_analyses', 'pm_settings', 'pm_user_profile',
    'pm_mileage_logs', 'pm_dismissed_items', 'pm_diy_guides', 'pm_aa_checklist',
    'pm_value_history', 'pm_reminders', 'pm_write_queue',
  ];
  keys.forEach((k) => localStorage.removeItem(k));
}
